import { Fragment, ReactNode } from 'react';

import Workflow from 'models/Workflow';
import { ActionStatus, WorkflowAction } from 'models/WorkflowAction';
import ChevronRight from 'svg/ChevronRight';

import { concludedByDenial } from '../referralProgress';
import { actionStatusDisplay } from '../StatusBadge';
import StatusIcon, { indicatorColor, isEmphasized, statusIndicator } from '../StatusIcon';

import { actionCardFor, hasSlideoutFor } from './actionCards';
import { ActionHandlers } from './handlers';
import { ActionRow, ChevronCell, IconCell, RowMain, RowTitle } from './styles';

/**
 * The shared chrome every action row wears (OLIO-2700): status icon, title, the kind's body, and
 * the slideout affordance. Deliberately contains no per-kind branching — what a kind contributes
 * lives in the actionCards registry, and the body is passed in.
 */
export default function ActionCard({
  action,
  workflow,
  body,
  slideoutOpen,
  onOpenSlideout,
  onCloseSlideout,
  handlers,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  body: ReactNode;
  slideoutOpen: boolean;
  onOpenSlideout: () => void;
  onCloseSlideout: () => void;
  handlers: ActionHandlers;
}) {
  // Slideout-backed rows turn the whole card into the trigger: clickable, chevron pinned far
  // right, and the slideout rendered as a sibling so its controls never nest in the button.
  const hasSlideout = hasSlideoutFor(action);
  const slideout = hasSlideout ? actionCardFor(action).slideout : undefined;
  // A row the denial concludes (the Referral Outcome itself, OLIO-3170) reads Denied — icon and
  // word — rather than the green "Complete" its status would give it, so the row agrees with the
  // red badge on the category above it and the rail beside it. Its siblings keep their own status.
  const denied = concludedByDenial(workflow, [action]);
  const indicator = denied ? 'denied' : statusIndicator(action);
  const statusLabel = denied ? 'Denied' : actionStatusDisplay(action.effectiveStatus).label;

  return (
    <Fragment>
      <ActionRow
        data-testid='action-row'
        data-action-kind={action.kind}
        $flagged={action.effectiveStatus === ActionStatus.FLAGGED}
        $clickable={hasSlideout}
        onClick={hasSlideout ? onOpenSlideout : undefined}>
        <IconCell>
          <StatusIcon indicator={indicator} size={16} />
        </IconCell>
        <RowMain>
          <RowTitle $color={indicatorColor(indicator)} $bold={isEmphasized(indicator)}>
            {statusLabel}: {action.displayLabel}
          </RowTitle>
          {body}
        </RowMain>
        {hasSlideout && (
          <ChevronCell>
            <ChevronRight width={14} height={14} />
          </ChevronCell>
        )}
      </ActionRow>
      {slideout?.({ action, workflow, handlers, slideoutOpen, onCloseSlideout })}
    </Fragment>
  );
}
